import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { UninstallBotModal } from '@components/UninstallAppModal/UninstallAppModal'
import { useMutation } from '@tanstack/react-query'
import { Box, Button, Icon, Paragraph, Stack, Text, Toggle } from '@ui'
import { useChannelEntitlements } from 'hooks/useChannelEntitlements'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    type Channel,
    SpaceAddressFromSpaceId,
    useBotChannels,
    useSpaceData,
    useTownsClient,
} from 'use-towns-client'
import type { Address } from 'viem'

import { usePanelActions } from './layouts/hooks/usePanelActions'

const useUpdateBotChannels = (spaceId: string, botClientAddress: string) => {
    const channels = useSpaceChannels()
    const { client } = useTownsClient()

    const channelIds = useMemo(() => channels.map((c) => c.id), [channels])
    const botChannels = useBotChannels(channelIds, botClientAddress)

    const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set())
    const [isInitialized, setIsInitialized] = useState(false)

    // Initialize selectedChannelIds once when channels are available
    useEffect(() => {
        if (!isInitialized && channels.length > 0) {
            setSelectedChannelIds(new Set(botChannels))
            setIsInitialized(true)
        }
    }, [isInitialized, channels.length, botChannels])

    const isAllChannelsSelected = useMemo(() => {
        return channels.length > 0 && selectedChannelIds.size === channels.length
    }, [channels, selectedChannelIds])

    const { channelsToAdd, channelsToRemove } = useMemo(() => {
        const currentChannels = new Set(botChannels)
        return {
            channelsToAdd: Array.from(selectedChannelIds.difference(currentChannels)),
            channelsToRemove: Array.from(currentChannels.difference(selectedChannelIds)),
        }
    }, [selectedChannelIds, botChannels])

    const hasChanges = channelsToAdd.length > 0 || channelsToRemove.length > 0

    const handleChannelToggle = useCallback((channelId: string, checked: boolean) => {
        setSelectedChannelIds((prev) => {
            const next = new Set(prev)
            if (checked) {
                next.add(channelId)
            } else {
                next.delete(channelId)
            }
            return next
        })
    }, [])

    const handleSelectAllChannels = useCallback(
        (checked: boolean) => {
            if (checked) {
                setSelectedChannelIds(new Set(channels.map((channel) => channel.id)))
            } else {
                setSelectedChannelIds(new Set())
            }
        },
        [channels],
    )

    const updateChannelsMutation = useMutation({
        mutationFn: async () => {
            if (!client || !botClientAddress) {
                throw new Error('Missing client or bot address')
            }
            const addPromises = channelsToAdd.map((channelId) =>
                client.joinUser(channelId, botClientAddress),
            )
            const removePromises = channelsToRemove.map((channelId) =>
                client.removeUser(channelId, botClientAddress),
            )
            await Promise.all([...addPromises, ...removePromises])
        },
        onSuccess: () => {
            popupToast(({ toast }) => (
                <StandardToast.Success toast={toast} message="Bot channels updated successfully" />
            ))
        },
        onError: (error) => {
            console.error('[BotSettingsPanel] Error updating bot channels:', error)
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="Failed to update bot channels" />
            ))
        },
    })

    const handleUpdateChannels = useCallback(() => {
        if (hasChanges) {
            updateChannelsMutation.mutate()
        }
    }, [hasChanges, updateChannelsMutation])

    return {
        channels,
        selectedChannelIds,
        isAllChannelsSelected,
        hasChanges,
        isUpdating: updateChannelsMutation.isPending,
        isLoadingChannels: channels.length === 0,
        handleChannelToggle,
        handleSelectAllChannels,
        handleUpdateChannels,
    }
}

const ChannelToggle = ({
    channel,
    spaceId,
    selected,
    disabled = false,
    onToggle,
}: {
    channel: Channel
    spaceId: string
    selected: boolean
    disabled?: boolean
    onToggle: (channelId: string, checked: boolean) => void
}) => {
    const { hasSomeEntitlement } = useChannelEntitlements({
        spaceId: spaceId,
        channelId: channel.id,
    })

    return (
        <Stack horizontal justifyContent="spaceBetween" alignItems="center">
            <Stack horizontal gap alignItems="center">
                <Icon
                    type={hasSomeEntitlement ? 'lock' : 'tag'}
                    padding="line"
                    background="level3"
                    size="square_lg"
                />
                <Stack gap="sm">
                    <Text color="gray1" textAlign="left">
                        {channel.label}
                    </Text>
                    {channel.topic && (
                        <Text color="gray2" size="sm">
                            {channel.topic}
                        </Text>
                    )}
                </Stack>
            </Stack>
            <Toggle
                toggled={selected}
                disabled={disabled}
                onToggle={(checked) => onToggle(channel.id, checked)}
            />
        </Stack>
    )
}

const SelectChannels = ({
    spaceId,
    channels,
    selectedChannelIds,
    isAllChannelsSelected,
    isUpdating,
    onChannelToggle,
    onSelectAllChannels,
}: {
    spaceId: string
    channels: Channel[]
    selectedChannelIds: Set<string>
    isAllChannelsSelected: boolean
    isUpdating: boolean
    onChannelToggle: (channelId: string, checked: boolean) => void
    onSelectAllChannels: (checked: boolean) => void
}) => {
    return (
        <Stack padding gap="lg" background="level2" rounded="sm">
            <Text fontWeight="strong" size="lg">
                Select Channels
            </Text>

            <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                <Text>All Channels</Text>
                <Toggle
                    toggled={isAllChannelsSelected}
                    disabled={isUpdating}
                    onToggle={onSelectAllChannels}
                />
            </Stack>

            <Stack gap="sm">
                {channels.map((channel) => {
                    const isSelected = selectedChannelIds.has(channel.id)
                    return (
                        <ChannelToggle
                            key={channel.id}
                            channel={channel}
                            spaceId={spaceId}
                            selected={isSelected}
                            disabled={isUpdating}
                            onToggle={onChannelToggle}
                        />
                    )
                })}
            </Stack>
        </Stack>
    )
}

const UninstallBot = ({
    appAddress,
    spaceAddress,
    spaceName,
}: {
    appAddress: Address
    spaceAddress: string
    spaceName?: string
}) => {
    const [open, onOpenChange] = useState(false)
    const { closePanel } = usePanelActions()

    return (
        <>
            <PanelButton tone="negative" onClick={() => onOpenChange(true)}>
                <Icon type="delete" size="square_sm" />
                <Paragraph>Uninstall Bot from {spaceName || 'Space'}</Paragraph>
            </PanelButton>
            <UninstallBotModal
                open={open}
                appAddress={appAddress}
                spaceAddress={spaceAddress}
                onOpenChange={onOpenChange}
                onSuccess={closePanel}
            />
        </>
    )
}

const UpdateChannelsButton = ({
    hasChanges,
    isUpdating,
    onUpdate,
}: {
    hasChanges: boolean
    isUpdating: boolean
    onUpdate: () => void
}) => {
    return (
        <Button tone="cta1" disabled={!hasChanges || isUpdating} width="100%" onClick={onUpdate}>
            {isUpdating ? <ButtonSpinner /> : 'Update'}
        </Button>
    )
}

export const BotSettingsPanel = () => {
    const [searchParams] = useSearchParams()

    const appAddress = searchParams.get('profileId') || ''
    const botClientAddress = searchParams.get('data') || ''

    const space = useSpaceData()
    const {
        channels,
        selectedChannelIds,
        isAllChannelsSelected,
        hasChanges,
        isUpdating,
        isLoadingChannels,
        handleChannelToggle,
        handleSelectAllChannels,
        handleUpdateChannels,
    } = useUpdateBotChannels(space?.id || '', botClientAddress)
    const spaceAddress = SpaceAddressFromSpaceId(space?.id || '')

    if (isLoadingChannels) {
        return (
            <Panel label="Bot Settings">
                <Box padding centerContent>
                    <ButtonSpinner />
                </Box>
            </Panel>
        )
    }

    if (!space || !spaceAddress) {
        return (
            <Panel label="Bot Settings">
                <Box padding centerContent>
                    <Text color="error">Space not found</Text>
                </Box>
            </Panel>
        )
    }

    return (
        <Panel label="Bot Settings">
            <Stack gap>
                <SelectChannels
                    spaceId={space.id}
                    channels={channels}
                    selectedChannelIds={selectedChannelIds}
                    isAllChannelsSelected={isAllChannelsSelected}
                    isUpdating={isUpdating}
                    onChannelToggle={handleChannelToggle}
                    onSelectAllChannels={handleSelectAllChannels}
                />

                <UninstallBot
                    appAddress={appAddress as Address}
                    spaceAddress={spaceAddress}
                    spaceName={space.name}
                />

                <UpdateChannelsButton
                    hasChanges={hasChanges}
                    isUpdating={isUpdating}
                    onUpdate={handleUpdateChannels}
                />
            </Stack>
        </Panel>
    )
}
